import { apiRequest, API_BASE_URL } from "./apiClient";

// AuthUser to include 2FA fields
export type AuthUser = {
  id: string;
  email: string;
  role: "user" | "admin";
  name?: string;
  profileImageUrl?: string;
  jobRole?: string;
  fieldOfWork?: string;
  provider?: "local" | "google" | "cognito";
  isOAuthAccount?: boolean;
  isEmailVerified?: boolean;
  isTwoFactorEnabled?: boolean;
  twoFactorMethod?: TwoFactorMethod;
};

export type LoginPayload = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export type RegisterPayload = {
  email: string;
  password: string;
  name?: string;
};

export type RegisterResponse = {
  user: AuthUser;
  message: string;
  requiresVerification: boolean;
};

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

export type UpdateProfilePayload = {
  name?: string;
  profileImageUrl?: string;
  jobRole?: string;
  fieldOfWork?: string;
};

export type VerifyEmailPayload = {
  token: string;
};

export type VerifyEmailResponse = {
  message: string;
  user: AuthUser;
};

export type ForgotPasswordPayload = {
  email: string;
};

export type ResetPasswordPayload = {
  token: string;
  newPassword: string;
};

export type CheckEmailProviderResponse = {
  exists: boolean;
  provider?: "local" | "google" | "cognito";
  isOAuthAccount: boolean;
  isAdmin?: boolean;
  requires2FA?: boolean;
};

export type MessageResponse = {
  message: string;
};

// ==========================================
// TWO-FACTOR AUTHENTICATION TYPES
// ==========================================

export type TwoFactorMethod = "totp" | "email" | "none";

export type TwoFactorSetupResponse = {
  method: "totp" | "email";
  qrCode?: string;
  secret?: string;
  otpauthUrl?: string;
  message?: string;
};

export type TwoFactorVerifySetupResponse = {
  message: string;
  backupCodes: string[];
};

export type TwoFactorStatusResponse = {
  isEnabled: boolean;
  method: TwoFactorMethod;
  backupCodesRemaining: number;
};

export type LoginResponse =
  | AuthUser
  | {
      requires2FA: true;
      method: TwoFactorMethod;
      tempToken: string;
      message: string;
    };

// Keep auth endpoints using direct fetch (no auto-refresh)
const authRequest = async <T>(path: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    let requestId =
      response.headers.get("x-request-id") ??
      response.headers.get("x-correlation-id") ??
      "";
    let message = "";
    let errorCode = "";

    if (contentType.includes("application/json")) {
      const data = (await response.json()) as {
        message?: string | string[];
        error?: string;
        code?: string;
        requestId?: string;
      };
      if (Array.isArray(data.message)) {
        message = data.message.join(", ");
      } else if (typeof data.message === "string") {
        message = data.message;
      } else if (typeof data.error === "string") {
        message = data.error;
      }
      errorCode = data.code || "";
      if (!requestId && data.requestId) {
        requestId = data.requestId;
      }
    } else {
      message = await response.text();
    }

    const statusLabel = `${response.status} ${response.statusText}`.trim();
    const requestIdSuffix = requestId ? ` [requestId: ${requestId}]` : "";
    const error = new Error(
      message
        ? `${statusLabel}: ${message}${requestIdSuffix}`
        : `${statusLabel}${requestIdSuffix}`,
    ) as Error & { code?: string };
    error.code = errorCode;
    throw error;
  }

  return response.json() as Promise<T>;
};

// ==========================================
// BASIC AUTH
// ==========================================

export const getMe = (options?: { skipRefresh?: boolean }) =>
  apiRequest<AuthUser>("/auth/me", { skipRefresh: options?.skipRefresh });

// login to return LoginResponse
export const login = (payload: LoginPayload) =>
  authRequest<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const checkEmailProvider = (email: string) =>
  apiRequest<CheckEmailProviderResponse>(
    `/auth/check-email?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
    },
  );

export const register = (payload: RegisterPayload) =>
  authRequest<RegisterResponse>("/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const logout = () =>
  apiRequest<{ loggedOut: boolean }>("/auth/logout", {
    method: "POST",
  });

export const refresh = () =>
  authRequest<AuthUser>("/auth/refresh", {
    method: "POST",
  });

// ==========================================
// EMAIL VERIFICATION
// ==========================================

export const verifyEmail = (payload: VerifyEmailPayload) =>
  authRequest<VerifyEmailResponse>("/auth/verify-email", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const resendVerification = (email: string) =>
  authRequest<MessageResponse>("/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

// ==========================================
// PASSWORD RESET
// ==========================================

export const forgotPassword = (payload: ForgotPasswordPayload) =>
  authRequest<MessageResponse>("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const resetPassword = (payload: ResetPasswordPayload) =>
  authRequest<MessageResponse>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });

// ==========================================
// PROFILE MANAGEMENT
// ==========================================

export const changePassword = (payload: ChangePasswordPayload) =>
  apiRequest<AuthUser>("/auth/change-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateProfile = (payload: UpdateProfilePayload) =>
  apiRequest<AuthUser>("/auth/update-profile", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const uploadProfileImage = (file: File) => {
  const formData = new FormData();
  formData.append("image", file);
  return apiRequest<AuthUser>("/auth/profile-image", {
    method: "POST",
    body: formData as unknown as BodyInit,
  });
};

// ==========================================
// ADMIN
// ==========================================

export const bootstrapAdmin = (payload: RegisterPayload, setupKey: string) =>
  authRequest<AuthUser>("/auth/setup/admin", {
    method: "POST",
    headers: { "x-admin-setup-key": setupKey },
    body: JSON.stringify(payload),
  });

// ==========================================
// TWO-FACTOR AUTHENTICATION API
// ==========================================

export const setup2FA = (method: "totp" | "email") =>
  apiRequest<TwoFactorSetupResponse>("/auth/2fa/setup", {
    method: "POST",
    body: JSON.stringify({ method }),
  });

export const verifySetup2FA = (code: string, method?: "totp" | "email") =>
  apiRequest<TwoFactorVerifySetupResponse>("/auth/2fa/verify-setup", {
    method: "POST",
    body: JSON.stringify({ code, method }),
  });

export const disable2FA = (password: string) =>
  apiRequest<MessageResponse>("/auth/2fa/disable", {
    method: "POST",
    body: JSON.stringify({ password }),
  });

export const get2FAStatus = () =>
  apiRequest<TwoFactorStatusResponse>("/auth/2fa/status");

export const regenerateBackupCodes = (password: string) =>
  apiRequest<{ message: string; backupCodes: string[] }>(
    "/auth/2fa/backup-codes",
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  );

export type TwoFactorLoginResponse = {
  user: AuthUser;
  backupCodesRemaining?: number;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
};

export const verify2FALogin = (
  tempToken: string,
  code: string,
  isBackupCode: boolean = false,
  rememberDevice: boolean = false,
) =>
  authRequest<TwoFactorLoginResponse>(
    "/auth/2fa/verify",
    {
      method: "POST",
      body: JSON.stringify({ tempToken, code, isBackupCode, rememberDevice }),
    },
  );

// Add new functions for managing trusted devices:

export type TrustedDevice = {
  deviceId: string;
  userAgent: string;
  createdAt: string;
  expiresAt: string;
};

export const getTrustedDevices = () =>
  apiRequest<TrustedDevice[]>("/auth/2fa/devices");

export const removeTrustedDevice = (deviceId: string) =>
  apiRequest<{ message: string }>(`/auth/2fa/devices/${deviceId}`, {
    method: "DELETE",
  });

export const removeAllTrustedDevices = () =>
  apiRequest<{ message: string }>("/auth/2fa/devices", {
    method: "DELETE",
  });

export const resend2FACode = (tempToken: string) =>
  authRequest<MessageResponse>("/auth/2fa/resend-code", {
    method: "POST",
    body: JSON.stringify({ tempToken }),
  });

export const googleAuthUrl = `${API_BASE_URL}/auth/google`;
